import { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { JourneyNodeData } from '../../utils/buildGraph';

/**
 * Custom React Flow node component for the IVR Journey diagram.
 * White card with coloured left border stripe indicating role.
 * Includes collapse/expand toggle for nodes with children.
 * BR-14: REQ-4, REQ-7, Visual Design Specification
 */
export const IVRNode = memo(({ data, selected }: NodeProps<JourneyNodeData>) => {
  const { configName, configType, roleColour, hasPendingDeletionWarning, childCount, isCollapsed, onToggleCollapse } = data as JourneyNodeData & { onToggleCollapse?: () => void };

  // Truncate name at 18 chars
  const displayName = configName.length > 18
    ? configName.slice(0, 18) + '…'
    : configName;

  const hasChildren = (childCount ?? 0) > 0;

  return (
    <>
      <Handle type="target" position={Position.Left} style={{ visibility: 'hidden' }} />
      <div
        className={`ivr-node ${selected ? 'ivr-node--selected' : ''}`}
        style={{
          width: 168,
          height: 56,
          background: '#FFFFFF',
          borderRadius: 8,
          borderLeft: `5px solid ${roleColour}`,
          boxShadow: selected
            ? `0 0 0 2px ${roleColour}, 0 4px 12px rgba(0,0,0,0.12)`
            : '0 1px 3px rgba(0,0,0,0.10), 0 1px 2px rgba(0,0,0,0.06)',
          padding: '10px 12px 10px 14px',
          cursor: 'pointer',
          position: 'relative',
          transition: 'box-shadow 0.15s ease, background 0.15s ease',
        }}
        title={configName} // Full name on hover
      >
        {/* Warning dot for pending-deletion references */}
        {hasPendingDeletionWarning && (
          <div
            style={{
              position: 'absolute',
              top: 8,
              right: hasChildren ? 24 : 8,
              width: 8,
              height: 8,
              background: '#E8890C',
              borderRadius: '50%',
            }}
          />
        )}

        {/* Collapse/expand toggle */}
        {hasChildren && onToggleCollapse && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              onToggleCollapse();
            }}
            style={{
              position: 'absolute',
              top: 6,
              right: 6,
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: `1.5px solid ${roleColour}`,
              background: isCollapsed ? roleColour : '#FFFFFF',
              color: isCollapsed ? '#FFFFFF' : roleColour,
              fontSize: 11,
              fontWeight: 700,
              lineHeight: '14px',
              textAlign: 'center',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 0,
            }}
            title={isCollapsed ? `Expand (${childCount} nodes)` : 'Collapse'}
            aria-label={isCollapsed ? `Expand ${childCount} hidden nodes` : 'Collapse sub-tree'}
          >
            {isCollapsed ? '+' : '−'}
          </button>
        )}

        {/* Config name */}
        <div
          style={{
            fontSize: 13,
            fontWeight: 700,
            color: '#1A2B45',
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            maxWidth: hasChildren ? 120 : 140,
          }}
        >
          {displayName}
        </div>

        {/* Type label + collapsed count badge */}
        <div
          style={{
            fontSize: 10,
            fontWeight: 600,
            color: '#94A3B8',
            textTransform: 'uppercase',
            letterSpacing: '0.4px',
            marginTop: 3,
            display: 'flex',
            alignItems: 'center',
            gap: 6,
          }}
        >
          {configType}
          {isCollapsed && childCount && (
            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: '#FFFFFF',
                background: roleColour,
                borderRadius: 8,
                padding: '1px 5px',
                textTransform: 'none',
              }}
            >
              +{childCount}
            </span>
          )}
        </div>
      </div>
      <Handle type="source" position={Position.Right} style={{ visibility: 'hidden' }} />
    </>
  );
});

IVRNode.displayName = 'IVRNode';
